import { ExchangeRateProvider } from './ExchangeRateContext';
import React, { createContext, useContext, useState, useEffect } from 'react';

// קבצי התרגום
import heTranslations from '../translations/he.json';
import enTranslations from '../translations/en.json';

const PublicLanguageContext = createContext();

const translations = {
  he: heTranslations,
  en: enTranslations
};

// פונקציה לזיהוי אוטומטי של שפת הדפדפן לפי מיקום האתר
const detectBrowserLanguage = () => {
  // בדיקת השפה המועדפת של הדפדפן
  const browserLanguage = navigator.language || navigator.languages?.[0] || '';
  
  // זיהוי המיקום מה-URL
  const currentPath = window.location.pathname;
  const isRothschildSite = currentPath.startsWith('/rothschild-booking');
  const isAirportSite = currentPath.startsWith('/airport-booking');
  
  // אם השפה היא עברית - תמיד עברית
  if (browserLanguage.toLowerCase().startsWith('he')) {
    return 'he';
  }
  
  // אם השפה היא אנגלית - תמיד אנגלית
  if (browserLanguage.toLowerCase().startsWith('en')) {
    return 'en';
  }
  
  // לכל שפה אחרת או כשלא מזוהה:
  if (isRothschildSite) {
    // באתר רוטשילד - עברית
    return 'he';
  } else if (isAirportSite) {
    // באתר איירפורט - אנגלית
    return 'en';
  }
  
  // ברירת מחדל כללית (אם לא באף אתר ציבורי) - עברית
  return 'he';
};

export const PublicLanguageProvider = ({ children, forcedLanguage }) => {
  // forcedLanguage: לדפים שהשפה שלהם קבועה לפי הכתובת (למשל /airport-booking/en)
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (forcedLanguage) return forcedLanguage;
    try {
      const saved = localStorage.getItem('publicSiteLanguage');
      if (['he', 'en'].includes(saved)) return saved;
    } catch { /* Storage can be disabled in private browsers. */ }
    return detectBrowserLanguage();
  });
  const [direction, setDirection] = useState(currentLanguage === 'he' ? 'rtl' : 'ltr');

  useEffect(() => {
    if (forcedLanguage) setCurrentLanguage(forcedLanguage);
  }, [forcedLanguage]);

  // שמירת שפה ב-localStorage כשהיא משתנה (עם מפתח נפרד לאתר הציבורי)
  useEffect(() => {
    try { localStorage.setItem('publicSiteLanguage', currentLanguage); } catch { /* Keep the language in memory. */ }
    const newDirection = currentLanguage === 'he' ? 'rtl' : 'ltr';
    setDirection(newDirection);
    
    // עדכון כיוון הדף רק אם אנחנו באתר הציבורי
    const isPublicSite = window.location.pathname.startsWith('/airport-booking') || 
                         window.location.pathname.startsWith('/rothschild-booking');
    if (isPublicSite) {
      document.documentElement.dir = newDirection;
      document.documentElement.lang = currentLanguage;
    }
  }, [currentLanguage]);

  // פונקציה לשינוי שפה
  const changeLanguage = (newLanguage) => {
    if (['he', 'en'].includes(newLanguage)) {
      setCurrentLanguage(newLanguage);
    }
  };

  // פונקציה לקבלת תרגום
  const translate = (key, defaultValue = key) => {
    const keys = key.split('.');
    let result = translations[currentLanguage];
    
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        return defaultValue;
      }
    }
    
    return result || defaultValue;
  };

  const value = {
    currentLanguage,
    direction,
    changeLanguage,
    translate,
    isRTL: direction === 'rtl'
  };

  return (
    <PublicLanguageContext.Provider value={value}>
      <ExchangeRateProvider enabled={currentLanguage === 'en'}>{children}</ExchangeRateProvider>
    </PublicLanguageContext.Provider>
  );
};

// Hook לשימוש בקונטקסט האתר הציבורי
export const usePublicLanguage = () => {
  const context = useContext(PublicLanguageContext);
  if (!context) {
    throw new Error('usePublicLanguage must be used within a PublicLanguageProvider');
  }
  return context;
};

// Hook קצר לתרגומים באתר הציבורי
export const usePublicTranslation = () => {
  const { translate } = usePublicLanguage();
  return translate;
};

export default PublicLanguageContext; 